/* eslint-disable sort-keys */
import Auth from '../managers/AuthManager';
import basicIframeModalLauncherManager from '../managers/BasicIframeModalLauncherManager';
import contentManager from '../managers/ContentManager';
import userManager from '../managers/UserManager';

import { DIALOG_NAMES } from '../managers/DialogManager';

import { AUTOLAUNCH_FLOATING_HELP_OPT_OUT_KEY, AUTOLAUNCH_FLOATING_HELP_SESSION_KEY } from '../constants/BasicIframeModalLauncherConstants';

import { registerClass } from '../SatCoreRegistry';
import {
  generateRobustUrl, getSessionStorageItem,
  removeSessionStorageItem, scrollBrowserWindowToTop
} from '../utils';

export default class BasicIframeModalLauncherService {
  static allowGettingStartedIframeModalLauncher = (iframeModalLauncherObj) => {
    const obj = iframeModalLauncherObj || this.getFloatingHelpIframeLauncherMapEntryValue();
    const isMicrosite = obj?.resourceType === 'microsite_resource';
    return obj?.url || (isMicrosite && obj?.resourceId);
  }

  static getDashboardGettingStartedIframeLauncherMapEntryValue = () => {
    if (userManager.isDistrictOrSchoolAdmin) {
      return basicIframeModalLauncherManager.basicIframeModalLauncherUrlMap?.get(
        'DASHBOARD_GETTING_STARTED_ADMIN'
      );
    } else if (userManager.isTeacher) {
      return basicIframeModalLauncherManager.basicIframeModalLauncherUrlMap?.get(
        'DASHBOARD_GETTING_STARTED_TEACHER'
      );
    } else if (userManager.isStudent) {
      return basicIframeModalLauncherManager.basicIframeModalLauncherUrlMap?.get(
        'DASHBOARD_GETTING_STARTED_STUDENT'
      );
    }
    return null;
  }

  static allowFloatingHelpIframeModalLauncher = (iframeModalLauncherObj) => {
    const obj = iframeModalLauncherObj || this.getFloatingHelpIframeLauncherMapEntryValue();
    const isMicrosite = obj?.resourceType === 'microsite_resource';
    return obj?.url || (isMicrosite && obj?.resourceId);
  }

  static getFloatingHelpIframeLauncherMapEntryValue = () => {
    if (userManager.isDistrictOrSchoolAdmin) {
      return basicIframeModalLauncherManager.basicIframeModalLauncherUrlMap?.get(
        'FLOATING_HELP_ADMIN'
      );
    } else if (userManager.isTeacher) {
      return basicIframeModalLauncherManager.basicIframeModalLauncherUrlMap?.get(
        'FLOATING_HELP_TEACHER'
      );
    } else if (userManager.isStudent) {
      return basicIframeModalLauncherManager.basicIframeModalLauncherUrlMap?.get(
        'FLOATING_HELP_STUDENT'
      );
    }
    return null;
  }

  static getDashboardGettingStartedIframeModalLauncherConfig = async (iframeModalLauncherObj) => {
    iframeModalLauncherObj = iframeModalLauncherObj || this.getDashboardGettingStartedIframeLauncherMapEntryValue();
    return this.generateIframeModalLauncherConfig(iframeModalLauncherObj);
  }

  static getFloatingHelpIframeModalLauncherConfig = async (iframeModalLauncherObj) => {
    iframeModalLauncherObj = iframeModalLauncherObj || this.getFloatingHelpIframeLauncherMapEntryValue();
    return this.generateIframeModalLauncherConfig(iframeModalLauncherObj);
  }

  static shouldAutoLaunchFloatingHelp = async (iframeModalLauncherObj) => {
    const obj = iframeModalLauncherObj || this.getFloatingHelpIframeLauncherMapEntryValue();
    const { allowAutoLaunch } = obj || {};

    if (!allowAutoLaunch || !this.allowFloatingHelpIframeModalLauncher(obj)) {
      return false;
    }

    // Skip while user identity is not yet established.
    if (!userManager.activePermissionId) {
      return false;
    }

    // Skip if this session already auto-launched (cleared on logout).
    if (getSessionStorageItem(AUTOLAUNCH_FLOATING_HELP_SESSION_KEY)) {
      return false;
    }

    const optedOut = await userManager.checkUserOptOut(AUTOLAUNCH_FLOATING_HELP_OPT_OUT_KEY);
    return !optedOut;
  }

  /**
   * If a pending /agentlink agentId exists in sessionStorage, open floating help (TESS)
   * and pass agentId to the microsite via postMessage. Clears the session key on success.
   * @returns {boolean} Whether the agent-link launch was attempted and succeeded
   */
  static launchFloatingHelpFromAgentLinkIfApplicable = async (dialogManager, {
    headerTitle,
    iframeModalLauncherObj
  } = {}) => {
    const agentId = getSessionStorageItem('c2c_agentId');
    if (!agentId || !dialogManager) {
      return false;
    }

    console.info('[AgentLink] Launcher hit with pending agentId', { agentId });

    const obj = iframeModalLauncherObj || this.getFloatingHelpIframeLauncherMapEntryValue();
    if (!this.allowFloatingHelpIframeModalLauncher(obj)) {
      console.info('[AgentLink] Launcher skipped — floating help not allowed for this user/config', {
        agentId,
        launcherId: obj?._id
      });
      return false;
    }

    const opened = await this.openIframeModalFromLauncher(
      dialogManager,
      obj, {
        additionalPostMessageParams: { agentId },
        headerTitle
      }
    );

    if (opened) {
      removeSessionStorageItem('c2c_agentId');
      console.info('[AgentLink] Deleted c2c_agentId from sessionStorage', { agentId });
    }

    return opened;
  }

  /**
   * Open a BasicIframeModal from a launcher (icon or button).
   *
   * Animation is FLIP-based (see useGenieTransition) — modal grows out of the
   * launcher rect returned by getOriginRect() on open, collapses back into it
   * on close. Satellites can opt out per-entry via `allowGenieTransition: false`
   * on the URL map entry.
   *
   * @param getOriginRect — callback returning the launcher's live DOMRect;
   *   passed through to BasicIframeModal → useGenieTransition. Skipped if the
   *   entry has allowGenieTransition === false.
   */
  static openIframeModalFromLauncher = async (dialogManager, iframeModalLauncherObj, {
    additionalPostMessageParams = {},
    closeOnDimmerClick = false,
    closeOnEscape = true,
    getOriginRect,
    headerTitle = 'External Content',
    iframeProps,
    initHeight,
    initWidth,
    onClose = () => {},
    onOpen = () => {},
    top,
    url
  } = {}) => {
    if (!dialogManager) {
      return false;
    }

    const config = url
      ? { url }
      : await this.generateIframeModalLauncherConfig(iframeModalLauncherObj);

    if (!config?.url) {
      return false;
    }

    const {
      url: resolvedUrl,
      postMessageParamObj,
      postMessageTargetOrigin
    } = config;

    const mergedPostMessageParamObj = {
      ...(postMessageParamObj || {}),
      ...(additionalPostMessageParams || {})
    };

    if (additionalPostMessageParams?.agentId) {
      console.info('[AgentLink] Launching microsite iframe', {
        launcherId: iframeModalLauncherObj?._id,
        postMessageParamObj: mergedPostMessageParamObj,
        postMessageTargetOrigin,
        resourceId: iframeModalLauncherObj?.resourceId,
        resourceType: iframeModalLauncherObj?.resourceType,
        url: resolvedUrl
      });
    }

    scrollBrowserWindowToTop();

    const launcherId = iframeModalLauncherObj?._id;
    const allowGenieTransition = iframeModalLauncherObj?.allowGenieTransition !== false;

    dialogManager.setOpenDialog(DIALOG_NAMES.BASIC_IFRAME_MODAL, {
      closeOnDimmerClick,
      closeOnEscape,
      getOriginRect: allowGenieTransition ? getOriginRect : undefined,
      headerTitle,
      iframeProps,
      initHeight,
      initWidth,
      launcherId,
      onClose: () => {
        dialogManager.closeDialog(DIALOG_NAMES.BASIC_IFRAME_MODAL);
        onClose();
      },
      onOpen: () => {
        onOpen();
      },
      postMessageParamObj: mergedPostMessageParamObj,
      postMessageTargetOrigin,
      top,
      url: resolvedUrl
    });

    return true;
  }

  static generateIframeModalLauncherConfig = async (iframeModalLauncherObj) => {
    if (!iframeModalLauncherObj) {
      return null;
    }

    const obj = iframeModalLauncherObj;
    let baseUrl;

    const isMicrosite = obj?.resourceType === 'microsite_resource';
    if (isMicrosite && obj.resourceId/* && obj.contentItemId */) {
      baseUrl = await contentManager.fetchMicrositeRedirectUrl({
        resourceId: obj.resourceId
      });
    } else {
      baseUrl = iframeModalLauncherObj.url;
    }

    if (!baseUrl) {
      return null;
    }

    // Build query params from customParamKeys
    const customParamObj = this.getIframeLauncherUrlCustomParamObj(obj.customParamKeys);
    const urlWithParams = generateRobustUrl(baseUrl, customParamObj);

    // Build postMessage params separately if configured
    const postMessageParamObj = this.getPostMessageParamObj(obj.postMessageParamKeys);

    // Always return an object for consistency
    return {
      postMessageParamObj,
      postMessageTargetOrigin: obj.postMessageTargetOrigin,
      url: urlWithParams
    };
  }

  static getIframeLauncherUrlCustomParamObj = (customParamKeys) => {
    if (Array.isArray(customParamKeys) && customParamKeys.length > 0) {
      const customParamObj = {
        ...(customParamKeys.includes('authKey') ? { authKey: Auth.authKey } : {}),
        ...(customParamKeys.includes('ecmsBaseUrl') ? { ecmsBaseUrl: Auth.ecms } : {}),
        ...(customParamKeys.includes('userId') ? { userId: userManager.userId } : {})
      };
      return customParamObj;
    }
    return {};
  }

  /**
   * Build parameter object for postMessage delivery to iframe
   * @param {Array} postMessageParamKeys - Array of parameter keys to include (e.g., ['authKey', 'userId'])
   * @returns {Object} Object containing the requested parameters
   */
  static getPostMessageParamObj = (postMessageParamKeys) => {
    if (Array.isArray(postMessageParamKeys) && postMessageParamKeys.length > 0) {
      const postMessageParamObj = {
        ...(postMessageParamKeys.includes('authKey') ? { authKey: Auth.authKey } : {}),
        ...(postMessageParamKeys.includes('ecmsBaseUrl') ? { ecmsBaseUrl: Auth.ecms } : {}),
        ...(postMessageParamKeys.includes('userId') ? { userId: userManager.userId } : {})
      };
      return postMessageParamObj;
    }
    return {};
  }
}

registerClass('BasicIframeModalLauncherService', BasicIframeModalLauncherService);
